import dayjs from 'dayjs';
import { useCallback, useRef, useState } from 'react';
import {
  Box,
  BoxProps,
  createVarsResolver,
  DataAttributes,
  ElementProps,
  factory,
  Factory,
  getRadius,
  MantineRadius,
  ScrollArea,
  ScrollAreaProps,
  StylesApiProps,
  UnstyledButton,
  useProps,
  useResolvedStylesApi,
  useStyles,
} from '@mantine/core';
import { useDatesContext } from '@mantine/dates';
import { useDragDropHandlers } from '../../hooks/use-drag-drop-handlers';
import { useSlotDragSelect } from '../../hooks/use-slot-drag-select';
import { ScheduleLabelsOverride } from '../../labels';
import {
  DateLabelFormat,
  DateStringValue,
  DateTimeStringValue,
  DayOfWeek,
  PreventEventOverlap,
  ScheduleCanDropEventData,
  ScheduleCanDropExternalEventData,
  ScheduleEventData,
  ScheduleEventPlacementRejectedData,
  ScheduleMode,
  ScheduleViewLevel,
} from '../../types';
import {
  calculateMonthDropDate,
  expandRecurringEvents,
  getMonthDays,
  getMonthRange,
  getWeekdaysNames,
  getWeekNumber,
  isSameMonth,
  toDateString,
} from '../../utils';
import { AgendaView, AgendaViewStylesNames } from '../AgendaView/AgendaView';
import { DragContext } from '../DragContext/DragContext';
import { MoreEvents, MoreEventsProps } from '../MoreEvents/MoreEvents';
import { ScheduleBackgroundEvent } from '../ScheduleBackgroundEvent';
import { RenderEvent, RenderEventBody, ScheduleEvent } from '../ScheduleEvent/ScheduleEvent';
import { MonthYearSelectProps } from '../ScheduleHeader/MonthYearSelect/MonthYearSelect';
import { CombinedScheduleHeaderStylesNames } from '../ScheduleHeader/ScheduleHeader';
import { ScheduleHeaderBase } from '../ScheduleHeader/ScheduleHeaderBase';
import { ViewSelectProps } from '../ScheduleHeader/ViewSelect/ViewSelect';
import { getMonthViewEvents } from './get-month-view-events/get-month-view-events';
import { getRenderableMonthEventSegments } from './get-renderable-month-event-segments';
import { clipSpanToVisibleColumns, getVisibleColumnMap } from './get-visible-columns';
import { handleMonthViewKeyDown, MonthViewControlsRef } from './handle-month-view-key-down';
import classes from './MonthView.module.css';

export type MonthViewStylesNames =
  | 'monthView'
  | 'monthViewScrollArea'
  | 'monthViewInner'
  | 'monthViewWeek'
  | 'monthViewDay'
  | 'monthViewDayLabel'
  | 'monthViewWeekNumber'
  | 'monthViewWeekday'
  | 'monthViewWeekdays'
  | 'monthViewWeekdaysCorner'
  | 'monthViewEvents'
  | 'monthViewBackgroundEvent'
  | CombinedScheduleHeaderStylesNames
  | AgendaViewStylesNames;

export type MonthViewCssVariables = {
  monthView: '--month-view-radius' | '--month-view-max-events';
};

export interface MonthViewProps
  extends BoxProps, StylesApiProps<MonthViewFactory>, ElementProps<'div'> {
  __staticSelector?: string;

  /** Date to display, Date object or date string in `YYYY-MM-DD 00:00:00` format */
  date: Date | string;

  /** Called with the new date value when a date is selected */
  onDateChange?: (value: DateStringValue) => void;

  /** If set, week numbers are displayed in the first column @default false */
  withWeekNumbers?: boolean;

  /** If set, weekdays names are displayed in the first row @default true */
  withWeekDays?: boolean;

  /** If set, weekend days are displayed. When `false`, days defined by `weekendDays` are hidden and the grid shrinks to the remaining columns @default true */
  withWeekendDays?: boolean;

  /** Locale passed down to dayjs, overrides value defined on `DatesProvider` */
  locale?: string;

  /** Number 0-6, where 0 – Sunday and 6 – Saturday. @default 1 – Monday */
  firstDayOfWeek?: DayOfWeek;

  /** `dayjs` format for weekdays names. By default, the first letter of the weekday. */
  weekdayFormat?: DateLabelFormat;

  /** Indices of weekend days, 0-6, where 0 is Sunday and 6 is Saturday. The default value is defined by `DatesProvider`. */
  weekendDays?: DayOfWeek[];

  /** Props passed down to the week number button */
  getWeekNumberProps?: (weekStartDate: DateStringValue) => Record<string, any>;

  /** Props passed down to the day button */
  getDayProps?: (date: DateStringValue) => Record<string, any>;

  /** Called when day is clicked */
  onDayClick?: (date: DateStringValue, event: React.MouseEvent<HTMLButtonElement>) => void;

  /** Called with first day of the week when week number is clicked */
  onWeekNumberClick?: (date: DateStringValue, event: React.MouseEvent<HTMLButtonElement>) => void;

  /** If set, always renders 6 weeks in the month view @default true */
  consistentWeeks?: boolean;

  /** If set, highlights the current day @default true */
  highlightToday?: boolean;

  /** Key of `theme.radius` or any valid CSS value to set `border-radius` @default theme.defaultRadius */
  radius?: MantineRadius;

  /** If set, days from the previous and next months are displayed to fill the weeks @default true */
  withOutsideDays?: boolean;

  /** If set, the header is displayed @default true */
  withHeader?: boolean;

  /** Props passed down to `MonthYearSelect` component in the header */
  monthYearSelectProps?: Partial<MonthYearSelectProps>;

  /** Called when view level select button is clicked */
  onViewChange?: (view: ScheduleViewLevel) => void;

  /** Props passed to previous month control */
  previousControlProps?: React.ComponentProps<'button'> & DataAttributes;

  /** Props passed to next month control */
  nextControlProps?: React.ComponentProps<'button'> & DataAttributes;

  /** Props passed to today control */
  todayControlProps?: React.ComponentProps<'button'> & DataAttributes;

  /** Props passed to view level select */
  viewSelectProps?: Partial<ViewSelectProps> & DataAttributes;

  /** Events to display */
  events?: ScheduleEventData[];

  /** Props passed down to `MoreEvents` component */
  moreEventsProps?: Partial<MoreEventsProps>;

  /** Function to customize event body, `event` object is passed as first argument */
  renderEventBody?: RenderEventBody;

  /** Function to fully customize event rendering, receives all props that would be passed to the root element including children */
  renderEvent?: RenderEvent;

  /** If true, events can be dragged and dropped @default false */
  withEventsDragAndDrop?: boolean;

  /** Called when event is dropped on new date */
  onEventDrop?: (data: {
    eventId: string | number;
    newStart: DateTimeStringValue;
    newEnd: DateTimeStringValue;
    event: ScheduleEventData;
  }) => void;

  /** Function to determine if event can be dragged */
  canDragEvent?: (event: ScheduleEventData) => boolean;

  /** Called while an event is dragged over a target to compute live feedback and again before the drop is committed, return `false` to reject the drop. Must be pure and cheap. */
  canDropEvent?: (data: ScheduleCanDropEventData) => boolean;

  /** Called while an external item is dragged over a target to compute live feedback and again before the drop is committed, return `false` to reject the drop. Must be pure and cheap. Only `dataTransfer.types` can be read while the drag is in progress. */
  canDropExternalEvent?: (data: ScheduleCanDropExternalEventData) => boolean;

  /** If set, drops that would make the event overlap another event are rejected. Pass a function to decide per pair of events: return `true` to forbid the overlap. The function runs while dragging to compute live feedback, it must be pure and cheap. @default false */
  preventEventOverlap?: PreventEventOverlap;

  /** Called when a drop is rejected */
  onEventPlacementRejected?: (data: ScheduleEventPlacementRejectedData) => void;

  /** Called when any event drag starts */
  onEventDragStart?: (event: ScheduleEventData) => void;

  /** Called when any event drag ends */
  onEventDragEnd?: () => void;

  /** Called when event is clicked */
  onEventClick?: (event: ScheduleEventData, e: React.MouseEvent<HTMLButtonElement>) => void;

  /** If set, background events (`display: 'background'`) can be clicked and trigger `onEventClick` @default false */
  withInteractiveBackgroundEvents?: boolean;

  /** If set, enables drag-to-select day ranges @default false */
  withDragSlotSelect?: boolean;

  /** Called when a day range is selected by dragging */
  onSlotDragEnd?: (rangeStart: DateTimeStringValue, rangeEnd: DateTimeStringValue) => void;

  /** Labels override for i18n */
  labels?: ScheduleLabelsOverride;

  /** Interaction mode: 'default' allows all interactions, 'static' disables event interactions @default default */
  mode?: ScheduleMode;

  /** Props passed down to `ScrollArea` component */
  scrollAreaProps?: Partial<ScrollAreaProps> & DataAttributes;

  /** Called when an external item is dropped onto the schedule. Receives the `DataTransfer` object and the drop target datetime. */
  onExternalEventDrop?: (dataTransfer: DataTransfer, dropDateTime: DateTimeStringValue) => void;

  /** Max number of generated recurring instances per recurring series @default 2000 */
  recurrenceExpansionLimit?: number;

  /** Maximum number of events visible per day before "+more" indicator shows, value is clamped between 1 and 10 @default 2 */
  maxEventsPerDay?: number;

  /** If set, displays an Agenda button in the header that opens an agenda list view @default false */
  withAgenda?: boolean;
}

export type MonthViewFactory = Factory<{
  props: MonthViewProps;
  ref: HTMLDivElement;
  stylesNames: MonthViewStylesNames;
  vars: MonthViewCssVariables;
}>;

const varsResolver = createVarsResolver<MonthViewFactory>(
  (_theme, { radius, maxEventsPerDay }) => ({
    monthView: {
      '--month-view-radius': radius !== undefined ? getRadius(radius) : undefined,
      '--month-view-max-events':
        maxEventsPerDay !== undefined ? `${Math.min(10, Math.max(1, maxEventsPerDay))}` : undefined,
    },
  })
);

const defaultProps = {
  __staticSelector: 'MonthView',
  withWeekDays: true,
  withWeekendDays: true,
  consistentWeeks: true,
  highlightToday: true,
  withOutsideDays: true,
  withHeader: true,
  weekdayFormat: 'ddd',
  withEventsDragAndDrop: false,
  withDragSlotSelect: false,
  mode: 'default',
  withInteractiveBackgroundEvents: false,
} satisfies Partial<MonthViewProps>;

export const MonthView = factory<MonthViewFactory>((_props) => {
  const props = useProps('MonthView', defaultProps, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    attributes,
    vars,
    mod,
    date,
    onDateChange,
    withWeekNumbers,
    withWeekDays,
    withWeekendDays,
    locale,
    weekdayFormat,
    firstDayOfWeek,
    weekendDays,
    __staticSelector,
    getDayProps,
    getWeekNumberProps,
    onDayClick,
    onWeekNumberClick,
    consistentWeeks,
    highlightToday,
    radius,
    withOutsideDays,
    withHeader,
    monthYearSelectProps,
    onViewChange,
    todayControlProps,
    nextControlProps,
    previousControlProps,
    viewSelectProps,
    events,
    moreEventsProps,
    renderEventBody,
    renderEvent,
    withEventsDragAndDrop,
    onEventDrop,
    canDragEvent,
    canDropEvent,
    canDropExternalEvent,
    preventEventOverlap,
    onEventPlacementRejected,
    onEventDragStart,
    onEventDragEnd,
    onEventClick,
    withInteractiveBackgroundEvents,
    withDragSlotSelect,
    onSlotDragEnd,
    labels,
    mode,
    scrollAreaProps,
    onExternalEventDrop,
    recurrenceExpansionLimit,
    maxEventsPerDay: _maxEventsPerDay,
    withAgenda,
    ...others
  } = props;

  const [agendaOpen, setAgendaOpen] = useState(false);
  const maxEventsPerDay = Math.min(10, Math.max(1, _maxEventsPerDay ?? 2));

  const getStyles = useStyles<MonthViewFactory>({
    name: __staticSelector,
    classes,
    props,
    className,
    style,
    classNames,
    styles,
    unstyled,
    attributes,
    vars,
    varsResolver,
    rootSelector: 'monthView',
  });

  const { resolvedClassNames, resolvedStyles } = useResolvedStylesApi<MonthViewFactory>({
    classNames,
    styles,
    props,
  });

  const stylesApiProps = {
    classNames: resolvedClassNames,
    styles: resolvedStyles,
    attributes,
    __staticSelector,
    radius,
  };

  const ctx = useDatesContext();

  const resolvedFirstDayOfWeek = ctx.getFirstDayOfWeek(firstDayOfWeek);
  const resolvedWeekendDays = ctx.getWeekendDays(weekendDays);
  const hiddenColumns = withWeekendDays
    ? []
    : Array.from({ length: 7 }, (_, index) => index).filter((index) =>
        resolvedWeekendDays.includes(((resolvedFirstDayOfWeek + index) % 7) as DayOfWeek)
      );
  const hiddenColumnSet = new Set(hiddenColumns);
  const columnsCount = 7 - hiddenColumns.length;
  const visibleColumnMap = getVisibleColumnMap(hiddenColumns);

  const range = getMonthRange({
    month: date,
    withOutsideDays,
    consistentWeeks,
    firstDayOfWeek: ctx.getFirstDayOfWeek(firstDayOfWeek),
  });

  const expandedEvents = expandRecurringEvents({
    events,
    rangeStart: dayjs(range.start).startOf('day').toDate(),
    rangeEnd: dayjs(range.end).endOf('day').toDate(),
    expansionLimit: recurrenceExpansionLimit,
  });

  const monthEvents = getMonthViewEvents({
    date,
    events: expandedEvents,
    firstDayOfWeek: ctx.getFirstDayOfWeek(firstDayOfWeek),
    withOutsideDays,
    consistentWeeks,
  });

  const getExternalDropDateTime = useCallback(
    (day: string) => `${dayjs(day).format('YYYY-MM-DD')} 00:00:00`,
    []
  );

  const handleExternalDrop = useCallback(
    (e: React.DragEvent, day: string) => {
      if (!onExternalEventDrop) {
        return;
      }
      onExternalEventDrop(e.dataTransfer, getExternalDropDateTime(day));
    },
    [onExternalEventDrop, getExternalDropDateTime]
  );

  const dragDrop = useDragDropHandlers<string>({
    enabled: withEventsDragAndDrop,
    mode,
    onEventDrop,
    canDragEvent,
    onEventDragStart,
    onEventDragEnd,
    calculateDropTarget: (targetDay: string, draggedEvent: ScheduleEventData) => {
      return calculateMonthDropDate({
        draggedEvent,
        targetDay,
      });
    },
    onExternalDrop: onExternalEventDrop ? handleExternalDrop : undefined,
    events: expandedEvents,
    preventEventOverlap,
    canDropEvent,
    canDropExternalEvent,
    onEventPlacementRejected,
    getExternalDropDateTime,
  });

  const withDragHandlers = (withEventsDragAndDrop || !!onExternalEventDrop) && mode !== 'static';

  const flatDaysRef = useRef<string[]>([]);

  const slotDragSelect = useSlotDragSelect({
    enabled: withDragSlotSelect && mode !== 'static',
    onDragEnd: (startIndex, endIndex) => {
      if (!onSlotDragEnd) {
        return;
      }
      const startDay = flatDaysRef.current[startIndex];
      const endDay = flatDaysRef.current[endIndex];
      if (startDay && endDay) {
        onSlotDragEnd(
          dayjs(startDay).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          dayjs(endDay).endOf('day').format('YYYY-MM-DD HH:mm:ss')
        );
      }
    },
  });

  const daysRef: MonthViewControlsRef = useRef<HTMLButtonElement[][]>([]);

  const monthDays = getMonthDays({
    month: dayjs(date).format('YYYY-MM-DD 00:00:00'),
    firstDayOfWeek: ctx.getFirstDayOfWeek(firstDayOfWeek),
    consistentWeeks: consistentWeeks && withOutsideDays,
  });

  const firstDayIndex = (() => {
    for (let weekIndex = 0; weekIndex < monthDays.length; weekIndex++) {
      const week = monthDays[weekIndex];
      let visibleDayIndex = -1;
      for (let dayIndex = 0; dayIndex < week.length; dayIndex++) {
        if (hiddenColumnSet.has(dayIndex)) {
          continue;
        }
        visibleDayIndex += 1;
        const day = week[dayIndex];
        const outside = !isSameMonth(date, day);
        if (!outside || withOutsideDays) {
          return { weekIndex, dayIndex: visibleDayIndex };
        }
      }
    }
    return { weekIndex: 0, dayIndex: 0 };
  })();

  const weekdays = withWeekDays
    ? getWeekdaysNames({
        locale: ctx.getLocale(locale),
        format: weekdayFormat,
        firstDayOfWeek: resolvedFirstDayOfWeek,
      })
        .map((day, index) => ({ day, index }))
        .filter(({ index }) => !hiddenColumnSet.has(index))
        .map(({ day, index }) => (
          <div {...getStyles('monthViewWeekday')} key={index}>
            {day}
          </div>
        ))
    : null;

  const monthGroup = 'month';
  flatDaysRef.current = monthDays
    .map((week) => week.filter((_, index) => !hiddenColumnSet.has(index)))
    .flat();

  const weeks = monthDays.map((week, weekIndex) => {
    daysRef.current[weekIndex] = [];

    const days = week
      .map((day, columnIndex) => ({ day, columnIndex }))
      .filter(({ columnIndex }) => !hiddenColumnSet.has(columnIndex))
      .map(({ day }, dayIndex) => {
        const outside = !isSameMonth(date, day);
        const weekend = resolvedWeekendDays.includes(dayjs(day).day());
        const ariaLabel = dayjs(day)
          .locale(locale || ctx.locale)
          .format('MMMM D, YYYY');

        const dayProps = getDayProps?.(dayjs(day).format('YYYY-MM-DD')) || {};
        const today = dayjs(day).isSame(dayjs(), 'day') && highlightToday;

        if (outside && !withOutsideDays) {
          return (
            <div key={day} data-static {...getStyles('monthViewDay', { style: dayProps.style })} />
          );
        }

        const isFirstDay =
          weekIndex === firstDayIndex.weekIndex && dayIndex === firstDayIndex.dayIndex;

        const isDropTarget = dragDrop.isDropTarget(day);
        const flatIndex = weekIndex * columnsCount + dayIndex;
        const isDragSelected = slotDragSelect.isSlotSelected(flatIndex, monthGroup);

        return (
          <UnstyledButton
            aria-label={ariaLabel}
            {...dayProps}
            {...getStyles('monthViewDay', { className: dayProps.className, style: dayProps.style })}
            key={day}
            ref={(node) => {
              if (node) {
                if (!daysRef.current[weekIndex]) {
                  daysRef.current[weekIndex] = [];
                }
                daysRef.current[weekIndex][dayIndex] = node;
              }
            }}
            data-drag-slot-index={withDragSlotSelect && mode !== 'static' ? flatIndex : undefined}
            data-drag-slot-group={withDragSlotSelect && mode !== 'static' ? monthGroup : undefined}
            onClick={
              mode === 'static'
                ? undefined
                : (event) => {
                    onDayClick?.(dayjs(day).format('YYYY-MM-DD'), event);
                    dayProps.onClick?.(event);
                  }
            }
            onPointerDown={
              withDragSlotSelect && mode !== 'static'
                ? (e) =>
                    slotDragSelect.handleSlotPointerDown(
                      e as React.PointerEvent<HTMLButtonElement>,
                      flatIndex,
                      monthGroup
                    )
                : undefined
            }
            onKeyDown={(event) => {
              handleMonthViewKeyDown({
                controlsRef: daysRef,
                weekIndex,
                dayIndex,
                event,
              });
              dayProps.onKeyDown?.(event);
            }}
            mod={[
              {
                outside,
                weekend,
                today,
                'drop-target': isDropTarget,
                'drag-selected': isDragSelected,
                static: mode === 'static',
              },
              dayProps.mod,
            ]}
            data-outside={outside || undefined}
            tabIndex={mode === 'static' ? -1 : isFirstDay ? 0 : -1}
            onDragOver={withDragHandlers ? (e) => dragDrop.handleDragOver(e, day) : undefined}
            onDragLeave={withDragHandlers ? dragDrop.handleDragLeave : undefined}
            onDrop={withDragHandlers ? (e) => dragDrop.handleDrop(e, day) : undefined}
          >
            <span data-today={today || undefined} {...getStyles('monthViewDayLabel')}>
              {dayjs(day).format('D')}
            </span>
          </UnstyledButton>
        );
      });

    const weekNumberProps = getWeekNumberProps?.(dayjs(week[0]).format('YYYY-MM-DD')) || {};
    const weekNumber = getWeekNumber(week);

    const interactiveBackgroundEvents = withInteractiveBackgroundEvents && mode !== 'static';

    const backgroundEventNodes = (monthEvents.backgroundByWeek[weekIndex] || []).flatMap(
      (event) => {
        const startColumn = Math.round(event.position.startOffset / (100 / 7));
        const span = Math.max(1, Math.round(event.position.width / (100 / 7)));
        const spans =
          hiddenColumns.length === 0
            ? [{ startOffset: event.position.startOffset, width: event.position.width }]
            : clipSpanToVisibleColumns(
                startColumn,
                startColumn + span - 1,
                visibleColumnMap,
                columnsCount
              );

        return spans.map((spanPosition, spanIndex) => (
          <ScheduleBackgroundEvent
            key={`bg-${event.id}-${weekIndex}-${spanIndex}`}
            event={event}
            renderEvent={renderEvent}
            renderEventBody={renderEventBody}
            interactive={interactiveBackgroundEvents}
            onEventClick={onEventClick}
            {...getStyles('monthViewBackgroundEvent', {
              style: {
                left: `calc(${spanPosition.startOffset}% + 2px)`,
                width: `calc(${spanPosition.width}% - 3px)`,
              },
            })}
          />
        ));
      }
    );

    const rowHeightPercent = 100 / maxEventsPerDay;

    const eventSegments = getRenderableMonthEventSegments({
      events: monthEvents.groupedByWeek[weekIndex] || [],
      groupedByDay: monthEvents.groupedByDay,
      maxEventsPerDay,
      week,
      hiddenColumns,
      columnsCount,
    });

    const events = eventSegments.map((segment) => {
      const isDraggable = dragDrop.isDraggableEvent(segment.event);

      return (
        <ScheduleEvent
          key={segment.key}
          event={segment.event}
          nowrap
          autoSize
          hanging={segment.position.hanging}
          draggable={isDraggable}
          renderEventBody={renderEventBody}
          renderEvent={renderEvent}
          radius={radius}
          mode={mode}
          mod={{ 'clip-start': segment.clipStart, 'clip-end': segment.clipEnd }}
          onClick={onEventClick ? (e) => onEventClick(segment.event, e) : undefined}
          style={{
            position: 'absolute',
            top: `calc(${segment.position.row * rowHeightPercent}% + 1px)`,
            left: `calc(${segment.position.startOffset}% + 1px)`,
            width: `calc(${segment.position.width}% - 2px)`,
            height: `calc(${rowHeightPercent}% - 2px)`,
          }}
        />
      );
    });

    const moreEventsNodes = week.map((day, dayIndex) => {
      if (hiddenColumnSet.has(dayIndex)) {
        return null;
      }

      const dayEvents = monthEvents.groupedByDay[day] || [];
      const hiddenEventsCount = Math.max(0, dayEvents.length - maxEventsPerDay);

      if (hiddenEventsCount <= 0) {
        return null;
      }

      const dayStartOffset = (visibleColumnMap[dayIndex] / columnsCount) * 100;
      const dayWidth = (1 / columnsCount) * 100;

      return (
        <MoreEvents
          key={`more-${day}`}
          events={dayEvents}
          moreEventsCount={hiddenEventsCount}
          mode={mode}
          labels={labels}
          renderEventBody={renderEventBody}
          renderEvent={renderEvent}
          onEventClick={onEventClick}
          style={{
            position: 'absolute',
            top: `calc(100% - 2px)`,
            left: `calc(${dayStartOffset}% + 1px)`,
            width: `calc(${dayWidth}% - 1px)`,
            height: '22px',
            paddingInline: 4,
          }}
          {...moreEventsProps}
        />
      );
    });

    return (
      <div {...getStyles('monthViewWeek')} key={weekIndex}>
        {withWeekNumbers && (
          <UnstyledButton
            key={weekNumber}
            aria-label={`Week ${weekNumber}`}
            {...weekNumberProps}
            onClick={
              mode === 'static'
                ? undefined
                : (event) => {
                    onViewChange?.('week');
                    onDateChange?.(toDateString(week[0]));
                    onWeekNumberClick?.(dayjs(week[0]).format('YYYY-MM-DD'), event);
                    weekNumberProps.onClick?.(event);
                  }
            }
            mod={{ static: mode === 'static' }}
            tabIndex={mode === 'static' ? -1 : 0}
            {...getStyles('monthViewWeekNumber', {
              className: weekNumberProps.className,
              style: weekNumberProps.style,
            })}
          >
            {weekNumber}
          </UnstyledButton>
        )}

        <div {...getStyles('monthViewEvents')} key="week-events">
          {backgroundEventNodes}
          {events}
          {moreEventsNodes}
        </div>

        {days}
      </div>
    );
  });

  const content = (
    <Box
      mod={[
        {
          'with-week-numbers': withWeekNumbers,
          'with-weekdays': withWeekDays,
          'without-weekend-days': hiddenColumns.length > 0,
          static: mode === 'static',
          'slot-dragging': slotDragSelect.isDragging,
          'event-interaction': dragDrop.dragContextValue.isDragging,
        },
        mod,
      ]}
      __vars={hiddenColumns.length > 0 ? { '--month-view-columns': `${columnsCount}` } : undefined}
      {...getStyles('monthView')}
      {...others}
    >
      {withHeader && (
        <ScheduleHeaderBase
          view="month"
          navigationHandlers={{
            previous: () => toDateString(dayjs(date).subtract(1, 'month').startOf('month')),
            next: () => toDateString(dayjs(date).add(1, 'month').startOf('month')),
            today: () => toDateString(dayjs()),
          }}
          control={{
            monthYearSelect: {
              locale,
              yearValue: dayjs(date).get('year'),
              monthValue: dayjs(date).get('month'),
              onYearChange: (year) =>
                onDateChange?.(toDateString(dayjs(date).set('year', year).startOf('month'))),
              onMonthChange: (month) =>
                onDateChange?.(toDateString(dayjs(date).set('month', month).startOf('month'))),
              ...monthYearSelectProps,
            },
          }}
          labels={labels}
          onDateChange={onDateChange}
          onViewChange={onViewChange}
          previousControlProps={previousControlProps}
          nextControlProps={nextControlProps}
          todayControlProps={todayControlProps}
          viewSelectProps={viewSelectProps}
          stylesApiProps={stylesApiProps}
          onAgendaClick={withAgenda ? () => setAgendaOpen((v) => !v) : undefined}
          agendaActive={agendaOpen}
        />
      )}

      {agendaOpen && (
        <AgendaView
          rangeStart={dayjs(date).startOf('month').format('YYYY-MM-DD')}
          rangeEnd={dayjs(date).endOf('month').format('YYYY-MM-DD')}
          events={events}
          locale={locale}
          labels={labels}
          mode={mode}
          onEventClick={onEventClick}
          recurrenceExpansionLimit={recurrenceExpansionLimit}
          {...stylesApiProps}
        />
      )}

      {!agendaOpen && (
        <ScrollArea
          scrollbarSize={4}
          {...scrollAreaProps}
          {...getStyles('monthViewScrollArea', {
            className: scrollAreaProps?.className,
            style: scrollAreaProps?.style,
          })}
        >
          <div {...getStyles('monthViewInner')}>
            {weekdays && (
              <div {...getStyles('monthViewWeekdays')}>
                {withWeekNumbers && <div {...getStyles('monthViewWeekdaysCorner')} />}
                {weekdays}
              </div>
            )}

            {weeks}
          </div>
        </ScrollArea>
      )}
    </Box>
  );

  if (withEventsDragAndDrop) {
    return <DragContext.Provider value={dragDrop.dragContextValue}>{content}</DragContext.Provider>;
  }

  return content;
});

MonthView.displayName = '@mantine/schedule/MonthView';
MonthView.classes = classes;
MonthView.varsResolver = varsResolver;

export namespace MonthView {
  export type Props = MonthViewProps;
  export type Factory = MonthViewFactory;
  export type StylesNames = MonthViewStylesNames;
  export type CssVariables = MonthViewCssVariables;
}
